import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import {
    LessFreightRecommendation,
    MoreFreightRecommendation,
    NetworkRecommendations,
    Recommendation,
    StartServiceRecommendation,
} from '../../../interfaces/services/networkRecommendations';
import { Lane } from '../../../interfaces/services/lanes';
import { getLaneId } from '../../stateHelpers';
import { formatNumber, formatRecommendationType } from '../../formattingUtils';
import { RecommendationType } from '../../enums';
import { FeatureTypes, MapSources } from '../enums';

/** Interface used for the properties object on the ShipperDetails Lanes Map Source. */
export interface NetworkRxMapSourceProperties {
    id: string;
    featureType: FeatureTypes;
    recommendationType?: RecommendationType;
    recommendationLaneId?: string;
    pickupServiceAreaName?: string | null;
    deliveryServiceAreaName?: string | null;
    targetPickupPartyName?: string | null;
    freightOrderOriginal?: number | null;
    description: string;
    color: string;
    strokeColor?: string;
    isHovered?: boolean;
}

/**
 * Loop through the networkRecommendations and add a point feature to the source for each lane origin and destination.
 * Loop through the networkRecommendations and add a line feature to the source for each lane.
 * @param networkRecommendations The network recommendations to show on the map
 * @param selectedRecommendation The selected recommendation to show on the map
 * @param hoveredLaneId The id of the hovered lane
 * @param theme MUI theme to use for styling
 * @returns A list of geojson features
*/
export const determineNetworkRecommendationsSource = ({
    networkRecommendations,
    selectedRecommendations,
    lanes,
    hoveredLaneId,
    theme,
}: {
    networkRecommendations: NetworkRecommendations;
    selectedRecommendations: (Recommendation | MoreFreightRecommendation | LessFreightRecommendation | StartServiceRecommendation)[];
    lanes: Lane[];
    hoveredLaneId: string | null;
    theme: Theme;
}): GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, NetworkRxMapSourceProperties>[] => {
    const features: GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, NetworkRxMapSourceProperties>[] = [];

    lanes.forEach((lane): void => {
        const laneId = getLaneId(lane);

        // by default the lanes are displayed as "hovered"
        let isHovered = true;
        if (hoveredLaneId) {
            if (laneId === hoveredLaneId) {
                isHovered = true;
            } else {
                isHovered = false;
            }
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value &&
            lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const laneTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Origin:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Destination:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                        [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                    ],
                },
                properties: {
                    id: laneId,
                    featureType: FeatureTypes.LaneLine,
                    description: laneTooltipHtml,
                    color: theme.palette.grey[600],
                    isHovered,
                },
            });
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value) {
            const pickupAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.pickup_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                },
                properties: {
                    id: lane.pickup_service_area_id,
                    featureType: FeatureTypes.LaneCenter,
                    description: pickupAreaTooltipHtml,
                    color: theme.palette.grey[500],
                    strokeColor: theme.palette.info.main,
                    isHovered,
                },
            });
        }

        if (lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const deliveryAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.delivery_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                },
                properties: {
                    id: lane.delivery_service_area_id,
                    featureType: FeatureTypes.LaneCenter,
                    description: deliveryAreaTooltipHtml,
                    color: theme.palette.grey[500],
                    strokeColor: theme.palette.info.main,
                    isHovered,
                },
            });
        }
    });

    const { move_more_freight, move_less_freight, start_service, stop_service } = networkRecommendations.recommendations;
    [...move_more_freight, ...move_less_freight, ...start_service, ...stop_service].forEach((lane): void => {
        // @ts-expect-error
        const laneId = getLaneId({
            pickup_service_area_id: lane.pickup_service_area_id,
            delivery_service_area_id: lane.delivery_service_area_id,
        });

        // by default the lanes are displayed as "hovered"
        let isHovered = true;
        if (hoveredLaneId) {
            if (laneId === hoveredLaneId) {
                isHovered = true;
            } else {
                isHovered = false;
            }
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value &&
            lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const laneTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Origin:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Destination:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                        [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                    ],
                },
                properties: {
                    id: laneId,
                    featureType: FeatureTypes.NetworkRecommendationLaneLine,
                    recommendationType: lane.recommendation_type,
                    recommendationLaneId: `${lane.recommendation_id}_${lane.lane_index}`,
                    pickupServiceAreaName: lane.pickup_service_area_name,
                    deliveryServiceAreaName: lane.delivery_service_area_name,
                    targetPickupPartyName: lane.target_pickup_party_name,
                    freightOrderOriginal: ('freight_order_original' in lane) ? lane.freight_order_original as number ?? 0 : 0,
                    description: laneTooltipHtml,
                    color: theme.palette.secondary.main,
                    isHovered,
                },
            });
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value) {
            const pickupAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.pickup_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                },
                properties: {
                    id: lane.pickup_service_area_id,
                    featureType: FeatureTypes.NetworkRecommendationLaneCenter,
                    recommendationType: lane.recommendation_type,
                    recommendationLaneId: `${lane.recommendation_id}_${lane.lane_index}`,
                    pickupServiceAreaName: lane.pickup_service_area_name,
                    deliveryServiceAreaName: lane.delivery_service_area_name,
                    targetPickupPartyName: lane.target_pickup_party_name,
                    description: pickupAreaTooltipHtml,
                    color: theme.palette.secondary.main,
                    strokeColor: theme.palette.info.main,
                    isHovered,
                },
            });
        }

        if (lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const deliveryAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.delivery_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                },
                properties: {
                    id: lane.delivery_service_area_id,
                    featureType: FeatureTypes.NetworkRecommendationLaneCenter,
                    recommendationType: lane.recommendation_type,
                    recommendationLaneId: `${lane.recommendation_id}_${lane.lane_index}`,
                    pickupServiceAreaName: lane.pickup_service_area_name,
                    deliveryServiceAreaName: lane.delivery_service_area_name,
                    targetPickupPartyName: lane.target_pickup_party_name,
                    description: deliveryAreaTooltipHtml,
                    color: theme.palette.secondary.main,
                    strokeColor: theme.palette.info.main,
                    isHovered,
                },
            });
        }
    });

    selectedRecommendations.forEach((lane, index): void => {
        // @ts-expect-error
        const laneId = getLaneId({
            pickup_service_area_id: lane.pickup_service_area_id,
            delivery_service_area_id: lane.delivery_service_area_id,
        });

        // by default the lanes are displayed as "hovered"
        let isHovered = true;
        if (hoveredLaneId) {
            if (laneId === hoveredLaneId) {
                isHovered = true;
            } else {
                isHovered = false;
            }
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value &&
            lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const renderAdditionalTooltipInfo = (): string => {
                if (lane.recommendation_type === RecommendationType.MoveMoreFreight || lane.recommendation_type === RecommendationType.MoveLessFreight) {
                    const typedLane = lane as MoreFreightRecommendation | LessFreightRecommendation;
                    return `
                        <div class='mapTooltipRow'>
                            <div class='mapTooltipCell'>${typedLane.target_pickup_party_name}</div>
                            <div class='mapTooltipCell'><strong>${formatNumber(typedLane.freight_order_original, 0, 0)} orders</strong></div>
                        </div>
                    `;
                }

                return '';
            };

            const laneTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <u>${formatRecommendationType(lane.recommendation_type)}</u>
                    </div>
                    <div class='mapTooltipRow'>
                        <strong>${lane.pickup_service_area_name} -> ${lane.delivery_service_area_name}</strong>
                    </div>
                    ${renderAdditionalTooltipInfo()}
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                        [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                    ],
                },
                properties: {
                    id: laneId,
                    featureType: FeatureTypes.SelectedRecommendationLaneLine,
                    recommendationType: lane.recommendation_type,
                    description: laneTooltipHtml,
                    color: theme.palette.secondary.main,
                    isHovered,
                },
            });
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value) {
            const pickupAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.pickup_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                },
                properties: {
                    id: lane.pickup_service_area_id,
                    featureType: FeatureTypes.SelectedRecommendationLaneCenter,
                    recommendationType: lane.recommendation_type,
                    description: pickupAreaTooltipHtml,
                    color: index === 0 ? theme.palette.success.pale : theme.palette.info.main,
                    strokeColor: index === 0 ? theme.palette.success.main : theme.palette.secondary.main,
                    isHovered,
                },
            });
        }

        if (lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const deliveryAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.delivery_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                },
                properties: {
                    id: lane.delivery_service_area_id,
                    featureType: FeatureTypes.SelectedRecommendationLaneCenter,
                    recommendationType: lane.recommendation_type,
                    description: deliveryAreaTooltipHtml,
                    color: index === selectedRecommendations.length - 1 ? theme.palette.error.pale : theme.palette.info.main,
                    strokeColor: index === selectedRecommendations.length - 1 ? theme.palette.error.main : theme.palette.secondary.main,
                    isHovered,
                },
            });
        }
    });

    return features;
};

/**
 * Loop through the networkRecommendations and add a point feature to the source for each lane origin and destination.
 * Loop through the networkRecommendations and add a line feature to the source for each lane.
 * Adds the networkRecommendations source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param networkRecommendations The network recommendations to show on the map
 * @param hoveredLaneId The id of the hovered lane
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addNetworkRxSource = ({
    map,
    networkRecommendations,
    selectedRecommendations,
    lanes,
    hoveredLaneId,
    theme,
}: {
    map: TrimbleMaps.Map;
    networkRecommendations: NetworkRecommendations;
    selectedRecommendations: (Recommendation | MoreFreightRecommendation | LessFreightRecommendation | StartServiceRecommendation)[];
    lanes: Lane[];
    hoveredLaneId: string | null;
    theme: Theme;
}): GeoJSONSourceRaw => {
    const features = determineNetworkRecommendationsSource({
        networkRecommendations,
        selectedRecommendations,
        lanes,
        hoveredLaneId,
        theme,
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features,
        },
    };

    if (map.getSource(MapSources.networkRecommendationsSource) === undefined) {
        map.addSource(MapSources.networkRecommendationsSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.networkRecommendationsSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
